import { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  ArrowRightIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ClockIcon,
  CodeBracketSquareIcon,
  EnvelopeIcon,
  MapPinIcon,
  PhoneIcon,
  ShieldCheckIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";

const contactCards = [
  {
    title: "Sales",
    description: "Talk through pricing, volumes, channels, and rollout timelines.",
    detail: "sales@whistlepulse.com",
    icon: SparklesIcon,
  },
  {
    title: "Support",
    description: "Get help with delivery, billing, dashboard access, or account setup.",
    detail: "support@whistlepulse.com",
    icon: ChatBubbleLeftRightIcon,
  },
  {
    title: "Developers",
    description: "Work with us on API keys, integrations, callbacks, and launch checks.",
    detail: "API integration support",
    icon: CodeBracketSquareIcon,
  },
];

const helpTopics = [
  "SMS, OTP, and bulk messaging setup",
  "Airtime campaigns and distribution workflows",
  "Sender IDs, throughput, and delivery questions",
  "Enterprise onboarding and technical integration",
];

function ContactUs() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    topic: "Sales inquiry",
    message: "",
  });
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      setError("Please add your name, email, and message.");
      setStatus("");
      setTimeout(() => setError(""), 3000);
      return;
    }

    setStatus("Thanks. Your message is ready for the Whistle Pulse team.");
    setError("");
    setFormData({
      name: "",
      email: "",
      company: "",
      topic: "Sales inquiry",
      message: "",
    });
    setTimeout(() => setStatus(""), 3500);
  };

  return (
    <div className="bg-slate-50">
      <Navbar />

      <section className="bg-[#071d33] text-white">
        <div className="mx-auto grid max-w-[85rem] gap-12 px-4 py-16 sm:px-6 lg:grid-cols-[0.95fr_1.05fr] lg:px-8 lg:py-24">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-blue-300">Contact Whistle Pulse</p>
            <h1 className="mt-5 text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
              Let’s get your messaging workflow moving.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-blue-50">
              Whether you need sales guidance, technical support, or help planning an integration, send the team a note
              and we’ll route it to the right people.
            </p>

            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {[
                [EnvelopeIcon, "Email", "support@whistlepulse.com"],
                [PhoneIcon, "Phone", "+233 548 036 178"],
                [ClockIcon, "Hours", "Mon-Fri, 9 AM-5 PM"],
                [MapPinIcon, "Region", "Serving businesses across Africa"],
              ].map(([Icon, title, value]) => (
                <div key={title} className="flex items-start gap-3 rounded-lg border border-white/10 bg-white/5 p-4">
                  <Icon className="h-6 w-6 shrink-0 text-blue-300" aria-hidden="true" />
                  <div>
                    <p className="text-sm text-blue-200">{title}</p>
                    <p className="mt-1 text-sm font-semibold text-white">{value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="rounded-2xl border border-white/10 bg-white p-5 text-slate-950 shadow-2xl shadow-black/20 sm:p-6">
            <div className="flex items-center justify-between gap-4 border-b border-slate-200 pb-5">
              <div>
                <h2 className="text-2xl font-bold">Send a message</h2>
                <p className="mt-1 text-sm text-slate-600">Tell us what you’re trying to build.</p>
              </div>
              <span className="hidden rounded-full bg-blue-50 px-3 py-1 text-sm font-semibold text-blue-700 sm:inline-flex">
                Fast response
              </span>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-slate-700">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  placeholder="Your name"
                  required
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-slate-700">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  placeholder="you@company.com"
                  required
                />
              </div>
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="company" className="block text-sm font-semibold text-slate-700">
                  Company
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  placeholder="Company name"
                />
              </div>
              <div>
                <label htmlFor="topic" className="block text-sm font-semibold text-slate-700">
                  Topic
                </label>
                <select
                  id="topic"
                  name="topic"
                  value={formData.topic}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                >
                  <option>Sales inquiry</option>
                  <option>Technical support</option>
                  <option>API integration</option>
                  <option>Billing question</option>
                  <option>Partnership</option>
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="message" className="block text-sm font-semibold text-slate-700">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="5"
                className="mt-2 w-full resize-none rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                placeholder="Share your goals, product, volume, countries, or technical question."
                required
              />
            </div>

            <button
              type="submit"
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100"
            >
              Send message
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
            </button>

            {status && <p className="mt-4 rounded-lg bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">{status}</p>}
            {error && <p className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</p>}
          </form>
        </div>
      </section>

      <section className="mx-auto max-w-[85rem] px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-4 md:grid-cols-3">
          {contactCards.map((card) => (
            <div key={card.title} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <card.icon className="h-8 w-8 text-blue-600" aria-hidden="true" />
              <h2 className="mt-5 text-xl font-bold text-slate-950">{card.title}</h2>
              <p className="mt-3 text-sm leading-6 text-slate-600">{card.description}</p>
              <p className="mt-5 font-semibold text-slate-950">{card.detail}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto grid max-w-[85rem] gap-10 px-4 pb-16 sm:px-6 lg:grid-cols-[0.85fr_1.15fr] lg:px-8">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.16em] text-blue-600">How we can help</p>
          <h2 className="mt-4 text-3xl font-bold text-slate-950 sm:text-4xl">
            Get practical guidance from setup to scale.
          </h2>
          <p className="mt-5 text-base leading-7 text-slate-600">
            Send enough context and we’ll help you choose the right product path, delivery setup, or integration plan.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {helpTopics.map((topic) => (
            <div key={topic} className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <CheckCircleIcon className="mt-0.5 h-6 w-6 shrink-0 text-blue-600" aria-hidden="true" />
              <p className="text-sm font-semibold leading-6 text-slate-700">{topic}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-[85rem] px-4 py-16 sm:px-6 lg:px-8">
          <div className="grid gap-6 rounded-2xl bg-[#071d33] p-6 text-white sm:p-8 lg:grid-cols-[0.7fr_1.3fr] lg:items-center">
            <div>
              <ShieldCheckIcon className="h-9 w-9 text-blue-300" aria-hidden="true" />
              <h2 className="mt-5 text-2xl font-bold">A better first conversation</h2>
            </div>
            <p className="text-sm leading-7 text-blue-50 sm:text-base">
              Include your expected monthly volume, destination countries, product channels, launch timeline, and any
              integration constraints. That helps the team respond with useful next steps quickly.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default ContactUs;
